// ─── 3-STAGE ELIGIBILITY ENGINE — extracted from AdminCCFSLeads.jsx (Phase 2b) ───
// State + API-owning component. Renders Stage 1 (CCFS Eligibility),
// Stage 2 (Sec 248 grounds + Sec 8 gate), Sec 249 restrictions and computed decision badge.
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronUp, ChevronDown, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  STAGE1_CHECKS, STAGE2_248_GROUNDS, STAGE3_249_RESTRICTIONS,
  DECISION_COLORS, DISPLAY_DECISION_LABEL, SOURCE_COLORS,
} from '@/pages/admin/ccfs/constants';

const API = process.env.REACT_APP_BACKEND_URL;

export function EligibilityEngine({ lead, onRefresh, readOnly = false }) {
  const existing = lead.eligibility_engine || {};
  const [s1, setS1] = useState(existing.stage1?.checks || {});
  const [s2Grounds, setS2Grounds] = useState(existing.stage2?.grounds || {});
  const [s3Restrictions, setS3Restrictions] = useState(existing.stage3?.restrictions || {});
  const [saving, setSaving] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [stage2Open, setStage2Open] = useState(false);

  // Compute pass/fail counts (used for stage1Open default + display)
  const s1Passed = STAGE1_CHECKS.filter(c => s1[c.key] === true).length;
  const s1Failed = STAGE1_CHECKS.filter(c => s1[c.key] === false).length;
  const [stage1Open, setStage1Open] = useState(() => {
    const passed = STAGE1_CHECKS.filter(c => (existing.stage1?.checks || {})[c.key] === true).length;
    // Always keep Stage 1 open for Continue intent (since Stage 2 is hidden, Stage 1 is the only content to see)
    const intentVal = (lead.quiz_answers?.[2] || lead.gap_rules_meta?.intent || lead.intent || '').toString().toLowerCase().trim();
    if (intentVal === 'continue') return true;
    return passed < STAGE1_CHECKS.length;
  });
  const [expandedGround, setExpandedGround] = useState(null);
  const [expandedRestriction, setExpandedRestriction] = useState(null);
  const [s8Status, setS8Status] = useState(existing.stage2?.section_8_block === true ? false : existing.stage2?.section_8_block === false ? true : null);

  // Derive Section 8 block from Stage 1 prefill, saved stage2, OR live s8Status button
  const s2Block = s1['not_section_8'] === false || existing.stage2?.section_8_block === true || s8Status === false;
  // Intent gating: read from quiz_answers[2] (primary — CCFS lead form: 'close' or 'continue'),
  // then gap_rules_meta.intent, then lead.intent. Only 'continue' hides Stage 2; everything else (close, empty, unknown) shows it.
  const intentCode = (
    lead.quiz_answers?.[2]
    || lead.gap_rules_meta?.intent
    || lead.intent
    || ''
  ).toString().toLowerCase().trim();
  const isClosureIntent = intentCode !== 'continue';

  // For Continue intent, derive an override decision so the badge reads "CCFS Eligible — Regularise"
  // (strike-off verdicts are meaningless when the client wants to continue).
  const effectiveDecision = !isClosureIntent
    ? 'ccfs_regularisation'
    : existing.decision;
  const effectiveDecisionLabel = !isClosureIntent
    ? 'CCFS Eligible — Regularise'
    : (DISPLAY_DECISION_LABEL[existing.decision] || existing.decision_label || existing.decision);

  const s2Applied = STAGE2_248_GROUNDS.filter(g => s2Grounds[g.key]?.status === 'applicable').length;
  const s2ToReview = STAGE2_248_GROUNDS.filter(g => {
    const gd = s2Grounds[g.key] || {};
    const pf = prefill?.stage2?.s248_grounds?.[g.key];
    return (!gd.status || gd.status === 'pending') && pf && (pf.confidence === 'high' || pf.confidence === 'medium');
  }).length;
  const s3Flagged = STAGE3_249_RESTRICTIONS.filter(r => s3Restrictions[r.key]?.status === 'flagged').length;
  const s3Clear = STAGE3_249_RESTRICTIONS.filter(r => s3Restrictions[r.key]?.status === 'clear').length;

  // Load prefill on mount
  useEffect(() => {
    const fetchPrefill = async () => {
      try {
        const token = localStorage.getItem('ratnatraya_token');
        const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/eligibility-prefill`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setPrefill(await res.json());
      } catch { /* ignore prefill errors */ }
    };
    if (lead.mca_company_info) fetchPrefill();
  }, [lead.id, lead.mca_company_info]);

  // Auto-apply prefill values to Stage 1 checks (only where auto=true and admin hasn't manually set)
  useEffect(() => {
    if (!prefill?.stage1) return;
    setS1(prev => {
      const updated = { ...prev };
      let changed = false;
      for (const [key, pf] of Object.entries(prefill.stage1)) {
        if (pf.auto && pf.value !== null && pf.value !== undefined && prev[key] === undefined) {
          updated[key] = pf.value;
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
    // Auto-apply Section 8 from prefill
    if (prefill.stage2?.not_section_8 && s8Status === null) {
      const pf = prefill.stage2.not_section_8;
      if (pf.auto && pf.value !== null && pf.value !== undefined) {
        setS8Status(pf.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const payload = {
        engine: {
          stage1: { checks: s1 },
          stage2: { grounds: s2Grounds, section_8_block: s8Status === false },
          stage3: { restrictions: s3Restrictions },
        },
      };
      const res = await fetch(`${API}/api/ccfs-leads/${lead.id}/eligibility-checklist`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Saved — ${data.decision_label}`);
        onRefresh?.();
      }
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const TriStateCheck = ({ checked, onChange, label, source, prefillData }) => (
    <div className={`flex items-center gap-2.5 py-2 px-3 rounded border transition-colors ${
      checked === true ? 'bg-emerald-50 border-emerald-200' :
      checked === false ? 'bg-red-50 border-red-200' :
      'bg-slate-50 border-slate-100'
    }`}>
      <div className="flex gap-1 shrink-0">
        <button onClick={() => !readOnly && onChange(checked === true ? null : true)} disabled={readOnly} className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition-all ${checked === true ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-300 border-slate-200 hover:border-emerald-400'} ${readOnly ? 'cursor-default' : ''}`}>Y</button>
        <button onClick={() => !readOnly && onChange(checked === false ? null : false)} disabled={readOnly} className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition-all ${checked === false ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-300 border-slate-200 hover:border-red-400'} ${readOnly ? 'cursor-default' : ''}`}>N</button>
      </div>
      <div className="flex-1 min-w-0">
        <span className={`text-xs ${checked === true ? 'text-emerald-700' : checked === false ? 'text-red-600' : 'text-slate-600'}`}>{label}</span>
        {prefillData?.note && (
          <p className="text-[9px] text-slate-400 mt-0.5 truncate">{prefillData.note}</p>
        )}
      </div>
      <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium shrink-0 ${SOURCE_COLORS[source] || 'bg-slate-100 text-slate-500'}`}>{source}</span>
      {prefillData?.auto && <span className="text-[9px] text-violet-500 font-medium shrink-0">Auto</span>}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* STAGE 1: CCFS Eligibility */}
      <div data-testid={`eligibility-stage1-${lead.id}`}>
        <button onClick={() => setStage1Open(!stage1Open)} className="w-full flex items-center justify-between py-2">
          <p className="text-[10px] text-emerald-600 uppercase tracking-wide font-bold flex items-center gap-1.5">
            Stage 1 — CCFS 2026 Eligibility
            <span className={`ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${s1Passed === STAGE1_CHECKS.length ? 'bg-emerald-100 text-emerald-700' : s1Failed > 0 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>
              {s1Passed}/{STAGE1_CHECKS.length} {s1Failed > 0 ? `(${s1Failed} failed)` : ''}
            </span>
          </p>
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${stage1Open ? 'bg-emerald-100' : 'bg-slate-100'}`}>
            {stage1Open ? <ChevronUp className="w-3.5 h-3.5 text-emerald-600" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
          </div>
        </button>
        {stage1Open && (
        <>
        <p className="text-[10px] text-slate-400 mt-1 mb-1">Y = condition met (eligible), N = condition not met (blocks eligibility)</p>
        <div className="space-y-1">
          {STAGE1_CHECKS.map(c => (
            <TriStateCheck
              key={c.key}
              checked={s1[c.key] ?? null}
              onChange={v => setS1(prev => ({ ...prev, [c.key]: v }))}
              label={c.label}
              source={c.source}
              prefillData={prefill?.stage1?.[c.key]}
            />
          ))}
        </div>
        </>
        )}
      </div>

      {/* STAGE 2 & 3: Only for closure/strike-off intent */}
      {isClosureIntent ? (
      <>
      {/* STAGE 2: Strike off eligibility check (Section 8 + 248(1) grounds + 249 restrictions) */}
      <div data-testid={`eligibility-stage2-${lead.id}`}>
        <button onClick={() => setStage2Open(!stage2Open)} className="w-full flex items-center justify-between py-2">
          <p className="text-[10px] text-amber-600 uppercase tracking-wide font-bold flex items-center gap-1.5">
            Stage 2 — Strike off eligibility check
            {s2Block && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-bold">BLOCKED — Sec 8</span>}
            {!s2Block && s2Applied > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold">{s2Applied} applicable</span>}
            {!s2Block && s2Applied === 0 && s2ToReview > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold">{s2ToReview} to review</span>}
            {!s2Block && s3Flagged > 0 && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-[9px] font-bold">{s3Flagged} Sec 249 flag(s)</span>}
            {!s2Block && s3Clear > 0 && s3Flagged === 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[9px] font-bold">Sec 249 clear</span>}
          </p>
          {stage2Open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </button>
        {stage2Open && (
          <div className="space-y-2 animate-in fade-in duration-200">
            <p className="text-[10px] text-slate-400 mb-1">Y = condition met / ground applies, N = condition not met / does not apply</p>
            {/* Section 8 Pre-qualification Gate — first check */}
            <div className={`rounded-lg border p-2.5 transition-colors mb-2 ${
              s8Status === true ? 'bg-emerald-50 border-emerald-200' :
              s8Status === false ? 'bg-red-50 border-red-300' :
              'bg-white border-slate-100'
            }`} data-testid="ground-s248_3">
              <div className="flex items-center gap-2.5">
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => !readOnly && setS8Status(s8Status === true ? null : true)} disabled={readOnly} className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition-all ${s8Status === true ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-300 border-slate-200 hover:border-emerald-400'} ${readOnly ? 'cursor-default' : ''}`} data-testid="s8-not-section8">Y</button>
                  <button onClick={() => !readOnly && setS8Status(s8Status === false ? null : false)} disabled={readOnly} className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition-all ${s8Status === false ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-300 border-slate-200 hover:border-red-400'} ${readOnly ? 'cursor-default' : ''}`} data-testid="s8-is-section8">N</button>
                </div>
                <div className="flex-1 min-w-0">
                  <span className={`text-xs ${s8Status === true ? 'text-emerald-700' : s8Status === false ? 'text-red-600' : 'text-slate-600'}`}>Company is NOT registered under Section 8 — Sec 248(3)</span>
                  {prefill?.stage2?.not_section_8?.note && (
                    <p className="text-[9px] text-slate-400 mt-0.5">{prefill.stage2.not_section_8.note}</p>
                  )}
                </div>
                <span className="text-[9px] px-1.5 py-0.5 rounded font-medium shrink-0 bg-violet-50 text-violet-600">CIN + MCA</span>
                {prefill?.stage2?.not_section_8?.auto && <span className="text-[9px] text-violet-500 font-medium shrink-0">Auto</span>}
              </div>
              {s8Status === false && (
                <p className="text-[9px] text-red-600 font-bold mt-1.5">BLOCKED — Section 8 company cannot apply for strike-off under 248(2). Sec 248(1) grounds and Sec 249 restrictions need not be checked. Regularisation via CCFS is still available.</p>
              )}
            </div>
            {/* 248(1) Grounds & 249 Restrictions — hidden when Section 8 blocks the application */}
            {!s2Block && (
            <>
            <p className="text-[10px] text-amber-600 font-bold uppercase tracking-wide mt-3 mb-1">Sec 248(1) Grounds — at least one must apply</p>
            <div className="space-y-2">
              {STAGE2_248_GROUNDS.map(g => {
                const gd = s2Grounds[g.key] || {};
                const pf = prefill?.stage2?.s248_grounds?.[g.key];
                const isExp = expandedGround === g.key;
                return (
                  <div key={g.key} className={`rounded-lg border p-2.5 transition-colors ${
                    gd.status === 'applicable' ? 'bg-emerald-50 border-emerald-200' :
                    gd.status === 'not_applicable' ? 'bg-slate-50 border-slate-200' :
                    'bg-white border-slate-100'
                  }`} data-testid={`ground-${g.key}`}>
                    <div className="flex items-start gap-2">
                      <button onClick={() => setExpandedGround(isExp ? null : g.key)} className="shrink-0 mt-0.5">
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold cursor-pointer hover:bg-amber-200 transition-colors">{g.clause}</span>
                      </button>
                      <span className="text-xs text-slate-700 flex-1 leading-relaxed">{g.label}</span>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => !readOnly && setS2Grounds(prev => ({...prev, [g.key]: {...(prev[g.key]||{}), status: gd.status === 'applicable' ? 'pending' : 'applicable'}}))}
                          disabled={readOnly}
                          className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition-all ${
                            gd.status === 'applicable' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-300 border-slate-200 hover:border-emerald-400'
                          } ${readOnly ? 'cursor-default' : ''}`} data-testid={`ground-yes-${g.key}`}>Y</button>
                        <button onClick={() => !readOnly && setS2Grounds(prev => ({...prev, [g.key]: {...(prev[g.key]||{}), status: gd.status === 'not_applicable' ? 'pending' : 'not_applicable'}}))}
                          disabled={readOnly}
                          className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition-all ${
                            gd.status === 'not_applicable' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-300 border-slate-200 hover:border-red-400'
                          } ${readOnly ? 'cursor-default' : ''}`} data-testid={`ground-no-${g.key}`}>N</button>
                      </div>
                    </div>
                    {pf && pf.evidence?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        {pf.likelihood && pf.likelihood !== 'uncertain' && (
                          <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                            pf.likelihood === 'likely' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                            'bg-slate-100 text-slate-600 border border-slate-300'
                          }`}>{pf.likelihood === 'likely' ? 'Likely ground' : 'Unlikely ground'}</span>
                        )}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                          pf.confidence === 'high' ? 'bg-blue-50 text-blue-700' :
                          pf.confidence === 'medium' ? 'bg-amber-50 text-amber-700' :
                          'bg-slate-50 text-slate-500'
                        }`}>{pf.confidence} confidence</span>
                        {pf.evidence.map((e, i) => (
                          <span key={`ev-${i}-${e}`} className="text-[9px] text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{e}</span>
                        ))}
                      </div>
                    )}
                    {!pf && !lead.mca_company_info && (
                      <p className="text-[9px] text-slate-400 mt-1">Upload MCA data for auto-assessment</p>
                    )}
                    {isExp && (
                      <div className="mt-2 space-y-1.5 pt-2 border-t border-slate-100 animate-in fade-in duration-150">
                        <div className="text-[9px] text-slate-500">
                          <span className="font-semibold">MCA indicators:</span> {g.indicators}
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                          <span className="text-[9px] text-slate-500 font-semibold">Further verification:</span>
                          {g.further_docs.map((d, i) => (
                            <span key={`doc-${i}-${d}`} className="text-[9px] text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">{d}</span>
                          ))}
                        </div>
                        <input
                          value={gd.notes || ''}
                          onChange={e => setS2Grounds(prev => ({...prev, [g.key]: {...(prev[g.key]||{}), notes: e.target.value}}))}
                          className="w-full text-[10px] px-2 py-1 rounded border border-slate-200 bg-white placeholder:text-slate-300"
                          placeholder="Admin notes..."
                          data-testid={`ground-notes-${g.key}`}
                        />
                        <p className="text-[9px] text-slate-400 italic">{g.note}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* 248(2) Computed Status — derived from grounds above */}
            <div className={`rounded-lg border p-2.5 ${
              s2Applied > 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-200'
            }`} data-testid="s248-2-status">
              <div className="flex items-center gap-2">
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-bold">Sec 248(2)</span>
                {s2Applied > 0 ? (
                  <span className="text-xs text-emerald-800 font-medium">{s2Applied} ground(s) applicable — 248(2) requirement met</span>
                ) : (
                  <span className="text-xs text-amber-700 font-medium">No grounds marked applicable — 248(2) requirement not satisfied yet</span>
                )}
              </div>
            </div>
            {/* Sec 249 Restrictions — nested inside Stage 2 */}
            <p className="text-[10px] text-red-500 font-bold uppercase tracking-wide mt-4 mb-1">Sec 249 Restrictions — all must be clear</p>
            <div className="space-y-2">
              {STAGE3_249_RESTRICTIONS.map(r => {
                const rd = s3Restrictions[r.key] || {};
                const pf = prefill?.stage3?.s249_restrictions?.[r.key];
                const isExp = expandedRestriction === r.key;
                return (
                  <div key={r.key} className={`rounded-lg border p-2.5 transition-colors ${
                    rd.status === 'clear' ? 'bg-emerald-50 border-emerald-200' :
                    rd.status === 'flagged' ? 'bg-red-50 border-red-200' :
                    'bg-white border-slate-100'
                  }`} data-testid={`restriction-${r.key}`}>
                    <div className="flex items-start gap-2">
                      <button onClick={() => setExpandedRestriction(isExp ? null : r.key)} className="shrink-0 mt-0.5">
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-bold cursor-pointer hover:bg-red-200 transition-colors">{r.clause}</span>
                      </button>
                      <span className="text-xs text-slate-700 flex-1 leading-relaxed">{r.label}</span>
                      <div className="flex gap-1 shrink-0">
                        <button onClick={() => !readOnly && setS3Restrictions(prev => ({...prev, [r.key]: {...(prev[r.key]||{}), status: rd.status === 'clear' ? 'pending' : 'clear'}}))}
                          disabled={readOnly}
                          className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition-all ${
                            rd.status === 'clear' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-white text-slate-300 border-slate-200 hover:border-emerald-400'
                          } ${readOnly ? 'cursor-default' : ''}`} data-testid={`restriction-yes-${r.key}`}>Y</button>
                        <button onClick={() => !readOnly && setS3Restrictions(prev => ({...prev, [r.key]: {...(prev[r.key]||{}), status: rd.status === 'flagged' ? 'pending' : 'flagged'}}))}
                          disabled={readOnly}
                          className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center border transition-all ${
                            rd.status === 'flagged' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-300 border-slate-200 hover:border-red-400'
                          } ${readOnly ? 'cursor-default' : ''}`} data-testid={`restriction-no-${r.key}`}>N</button>
                      </div>
                    </div>
                    {pf && pf.evidence?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-1.5">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                          pf.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' :
                          pf.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-500'
                        }`}>{pf.confidence} confidence</span>
                        {pf.evidence.map((e, i) => (
                          <span key={`ev-${i}-${e}`} className="text-[9px] text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">{e}</span>
                        ))}
                      </div>
                    )}
                    {isExp && (
                      <div className="mt-2 space-y-1.5 pt-2 border-t border-slate-100 animate-in fade-in duration-150">
                        <div className="text-[9px] text-slate-500">
                          <span className="font-semibold">Primary indicators:</span> {r.indicators}
                        </div>
                        <div className="flex flex-wrap gap-1 items-center">
                          <span className="text-[9px] text-slate-500 font-semibold">Further verification:</span>
                          {r.further_docs.map((d, i) => (
                            <span key={`doc-${i}-${d}`} className="text-[9px] text-slate-500 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">{d}</span>
                          ))}
                        </div>
                        <input
                          value={rd.notes || ''}
                          onChange={e => setS3Restrictions(prev => ({...prev, [r.key]: {...(prev[r.key]||{}), notes: e.target.value}}))}
                          className="w-full text-[10px] px-2 py-1 rounded border border-slate-200 bg-white placeholder:text-slate-300"
                          placeholder="Admin notes..."
                          data-testid={`restriction-notes-${r.key}`}
                        />
                        <p className="text-[9px] text-slate-400 italic">{r.note}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </>
            )}
          </div>
        )}
      </div>
      </>
      ) : (
        <div className="py-3 px-4 rounded-lg bg-amber-50 border border-amber-200" data-testid="eligibility-continue-banner">
          <p className="text-xs font-bold text-amber-800">Strike-off eligibility check not applicable — Intent is to Continue</p>
          <p className="text-[10px] text-amber-700 mt-1">Change lead intent to &quot;Close Company&quot; (via Lead details / Discovery Call) to enable Sec 248(1) grounds and Sec 249 restriction checks.</p>
        </div>
      )}

      {/* Decision + Save */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        {(existing.decision || !isClosureIntent) && (
          <div className={`text-xs font-bold px-3 py-1.5 rounded-lg border ${DECISION_COLORS[effectiveDecision] || 'bg-slate-100 text-slate-600'}`} data-testid={`engine-decision-${lead.id}`}>
            {effectiveDecisionLabel}
            {isClosureIntent && existing.decision_reason && (
              <span className="ml-1.5 text-[10px] font-normal opacity-75">— {existing.decision_reason}</span>
            )}
            {!isClosureIntent && (
              <span className="ml-1.5 text-[10px] font-normal opacity-75">— Strike-off N/A</span>
            )}
          </div>
        )}
        {!readOnly && (
        <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs gap-1 bg-[#1e3a5f] hover:bg-[#2a4f7a] ml-auto" data-testid={`save-engine-${lead.id}`}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save & Compute Decision
        </Button>
        )}
      </div>
    </div>
  );
}
